import {
  DeleteConfirmation,
  ExportDialog,
  Icon,
  SearchInput,
  ExportDropdown,
  Page,
  BackLink,
  IconButton,
  FiltersBar,
  ListView,
  ListViewActions,
  ListViewMenu,
  TaskCategoryIcon,
  Tooltip,
} from '~/components';
import { useApi, useConfirmation, useToast, useWorkspace } from '~/contexts';
import { useDocumentTitle } from '~/hooks';
import projectTaskCategoryTypes from '~/lookups/project-task-category-types';
import React, { useCallback, useEffect, useState } from 'react';
import { ErrorPage, PageLoader } from '~/routes/public/pages';
import styled from 'styled-components';
import { mimeTypes } from '~/utils';
import { ActiveStatusSelect } from '../custom-data/components';
import ProjectTaskCategoryDrawer from './ProjectTaskCategoryDrawer';

const TypeName = styled.span`
  display: flex;
  align-items: center;
`;

const DefaultTooltip = styled(Tooltip)`
  display: inline;
  margin-left: 0.5rem;
`;

function ProjectTaskCategoriesPage() {
  const documentTitle = useDocumentTitle('Task Categories');

  const api = useApi();
  const { workspace } = useWorkspace();
  const confirmation = useConfirmation();
  const toast = useToast();

  const [query, setQuery] = useState({ isReady: false, data: null, error: null });
  const [params, setParams] = useState({ q: '', isActive: 'true', size: 1000 });

  const fetchData = useCallback(async () => {
    try {
      const { data } = await api.www
        .workspaces(workspace.id)
        .projectTaskCategories()
        .get({ ...params, isActive: params.isActive ?? undefined });
      setQuery({ isReady: true, data });
    } catch (error) {
      setQuery({ isReady: true, data: null, error });
    }
  }, [workspace.id, params, api]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const [dialog, setDialog] = useState(null);

  const handleFilterChange = ({ target: { name, value } }) => {
    setParams((state) => ({ ...state, [name]: value }));
  };

  const handleExport = async (filename, mimeType) => {
    confirmation.prompt((resolve) => (
      <ExportDialog
        filename={filename}
        onClose={resolve}
        onLoad={api.www
          .workspaces(workspace.id)
          .projectTaskCategories()
          .get(
            { ...params, isActive: params.isActive ?? undefined },
            { headers: { accept: mimeType }, responseType: 'blob' },
          )}
      />
    ));
  };

  const handleEdit = async (taskCategory) => {
    setDialog(
      <ProjectTaskCategoryDrawer
        taskCategory={taskCategory}
        onClose={() => {
          setDialog(null);
          documentTitle.set('Task Categories');
        }}
        onDeleted={fetchData}
        onSaved={fetchData}
      />,
    );
  };

  async function handleActiveStatusChange(item, flag) {
    try {
      await api.www.workspaces(workspace.id).projectTaskCategories(item.id).setActiveStatus(flag);
      await fetchData();
    } catch ({ message }) {
      toast.error(message);
    }
  }

  async function handleDefaultStatusChange(item) {
    try {
      await api.www.workspaces(workspace.id).projectTaskCategories(item.id).setDefaultStatus();
      await fetchData();
    } catch ({ message }) {
      toast.error(message);
    }
  }

  async function handleDelete(taskCategory) {
    const confirm = await confirmation.prompt((resolve) => (
      <DeleteConfirmation resolve={resolve}>Are you sure you want to delete this Task Category?</DeleteConfirmation>
    ));
    if (!confirm) return;

    try {
      await api.www
        .workspaces(workspace.id)
        .projectTaskCategories(taskCategory ? taskCategory.id : undefined)
        .delete();
      await fetchData();
    } catch ({ message }) {
      toast.error(message);
    }
  }

  if (!query.isReady) return <PageLoader />;
  if (!query.data) return <ErrorPage publicSite={false} />;

  const data = query.data;

  return (
    <>
      <Page.Header>
        <BackLink defaultPath={`/app/${workspace.key}/settings/custom-data`} />

        <Page.Info>
          <Page.Title>Task Categories</Page.Title>
          <Page.Description>
            Task categories allow you to categorize tasks within projects. Time can be tracked directly to a standard
            task while a rollup task aggregates time from its child tasks.
          </Page.Description>
        </Page.Info>

        <Page.Actions>
          <ExportDropdown>
            {({ setIsOpen }) => (
              <>
                <ExportDropdown.Item
                  onClick={async () => {
                    await handleExport('task_categories.csv', mimeTypes.csv);
                    setIsOpen(false);
                  }}>
                  Export to CSV
                </ExportDropdown.Item>

                <ExportDropdown.Item
                  onClick={async () => {
                    await handleExport('task_categories.xlsx', mimeTypes.xlsx);
                    setIsOpen(false);
                  }}>
                  Export to Excel
                </ExportDropdown.Item>
              </>
            )}
          </ExportDropdown>

          <IconButton icon="plus" tooltip="New Task Category" onClick={() => handleEdit({})} />
        </Page.Actions>
      </Page.Header>

      <Page.Filters>
        <FiltersBar>
          <SearchInput value={params.q} placeholder="Search" onChange={handleFilterChange} />

          <ActiveStatusSelect value={params.isActive} onChange={handleFilterChange} />
        </FiltersBar>
      </Page.Filters>

      <Page.ListView>
        <ListView>
          <ListView.Header>
            <ListView.Column width="2.5rem" />
            <ListView.Column width="15rem">Name</ListView.Column>
            <ListView.Column>Type</ListView.Column>
            <ListViewActions.Column />
          </ListView.Header>
          <ListView.Body>
            {data.map((taskCategory) => {
              const { id, name, icon, color, type, isActive, isDefault } = taskCategory;

              return (
                <ListView.Row onClick={() => handleEdit(taskCategory)} key={id} isDisabled={!isActive}>
                  <ListView.Cell>
                    <TaskCategoryIcon icon={icon} color={color} />
                  </ListView.Cell>
                  <ListView.Cell>
                    <TypeName>
                      {name}
                      {isDefault && (
                        <DefaultTooltip placement="right" message="The default task category for new tasks.">
                          <Icon icon="badge-check" />
                        </DefaultTooltip>
                      )}
                    </TypeName>
                  </ListView.Cell>
                  <ListView.Cell>{projectTaskCategoryTypes[type].name}</ListView.Cell>
                  <ListViewActions>
                    <>
                      <ListViewActions.Edit onClick={() => handleEdit(taskCategory)} />

                      <hr />

                      <ListViewMenu>
                        {({ setIsOpen }) => (
                          <>
                            <ListViewMenu.Item onClick={() => handleEdit(taskCategory)}>Edit</ListViewMenu.Item>
                            {!isDefault && (
                              <>
                                {isActive && type === 'standard' && (
                                  <ListViewMenu.Item
                                    onClick={async () => {
                                      await handleDefaultStatusChange(taskCategory);
                                      setIsOpen(false);
                                    }}>
                                    Set as default
                                  </ListViewMenu.Item>
                                )}
                                <ListViewMenu.Item
                                  onClick={async () => {
                                    await handleActiveStatusChange(taskCategory, !isActive);
                                    setIsOpen(false);
                                  }}>
                                  {isActive ? 'Deactivate' : 'Activate'}
                                </ListViewMenu.Item>
                                <ListViewMenu.DeleteItem
                                  tooltip="This item is currently in use."
                                  onCheckDependencies={async (workspace) =>
                                    (await workspace.projectTaskCategories(id).hasDependencies()).data
                                  }
                                  onClick={() => handleDelete(taskCategory)}>
                                  Delete
                                </ListViewMenu.DeleteItem>
                              </>
                            )}
                          </>
                        )}
                      </ListViewMenu>
                    </>
                  </ListViewActions>
                </ListView.Row>
              );
            })}

            {data.length === 0 && <ListView.Empty />}
          </ListView.Body>
          <ListView.Status total={data.length} label="Task Category" />
        </ListView>
      </Page.ListView>

      {dialog}
    </>
  );
}

export default ProjectTaskCategoriesPage;
